import { Block } from '../../components/layout/Block';
import { Props } from './props';
import { CheckPermissionFunction, PermissionGate } from '../../../../Auth/components/PermissionGate';
import { Actions, MultiInputControls } from '../../../components/form/MultiInput/MultiInputControls';
import { EventDTO, EventPlaceDTO } from '../../../../api/core';
import { getDefaultEvent, getDefaultEventPlace } from '../../../../Settings/emptyModels';
import React, { ReactElement } from 'react';
import { JustifiedDeleteButton } from '../../../components/form/MultiInput/Buttons';
import { InputGroup } from '../../components/layout/InputGroup';
import { Fieldset } from '../../components/layout/Fieldset';
import { LabelledInputText } from '../../../components/form/LabelledInputText';
import { Panel } from '../../components/layout/Panel';
import { LabelledCalendar } from '../../../components/form/LabelledCalendar';

export const EventsPage = (props: Props): ReactElement => {
  return (
    <Block title={`work.header.events`}>
      <PermissionGate always={false} entity={props.permissionEntity} field="events">
        {(checkPermission: CheckPermissionFunction) => (
          <div className={'col-12'}>
            <Panel title={'work.attr.events'}>
              <MultiInputControls<EventDTO>
                canEdit={checkPermission().canWrite}
                lines={props.modifiedModel.events || []}
                addNew={(rows: EventDTO[]): number => rows.push(getDefaultEvent())}
                onChange={(events: EventDTO[]): void => props.setField('events', events)}
                renderLines={(
                  event: EventDTO,
                  onEventChange: (newValues: EventDTO) => void,
                  actions: Actions,
                  idx: number
                ): ReactElement => (
                  <Fieldset title={'work.attr.events.attr.event'}>
                    <div className={'flex flex-column gap-4'}>
                      <LabelledInputText
                        id={`events-${idx}-name`}
                        autoFocus={props.autoFocus}
                        className={'w-12'}
                        value={event.eventName}
                        permissions={checkPermission('event_name')}
                        validation={props.validation?.root?.fields?.events.children[idx].fields?.event_name}
                        onChange={(eventName: string): void => onEventChange({ ...event, eventName })}
                        label={'work.attr.event.attr.eventName'}
                      />
                      <Fieldset title={'work.attr.event.attr.places'}>
                        <MultiInputControls<EventPlaceDTO>
                          canEdit={checkPermission().canWrite}
                          onChange={(eventPlaces: EventPlaceDTO[]): void => onEventChange({ ...event, eventPlaces })}
                          lines={event.eventPlaces || []}
                          addNew={(places: EventPlaceDTO[]): number => places.push(getDefaultEventPlace())}
                          renderLines={(
                            eventPlaceEntity: EventPlaceDTO,
                            onPlaceChange: (newValue: EventPlaceDTO) => void,
                            placeActions: Actions,
                            idx2: number,
                            lines: EventPlaceDTO[],
                            onChangeAll: (
                              newValues: EventPlaceDTO[],
                              changedValues: EventPlaceDTO[],
                              originals?: EventPlaceDTO[]
                            ) => void
                          ): React.ReactNode => (
                            <InputGroup
                              delete={{
                                action: placeActions.delete,
                                disabled: !checkPermission('event_places').canWrite,
                              }}
                            >
                              <LabelledInputText
                                id={`events-${idx}-place-${idx2}-placeName`}
                                autoFocus={props.autoFocus}
                                value={eventPlaceEntity.eventPlace}
                                permissions={checkPermission('event_places.event_place')}
                                validation={
                                  props.validation?.root?.fields?.events?.children[idx].fields?.event_places.children[
                                    idx2
                                  ].fields?.event_place
                                }
                                onChange={(eventPlace: string): void =>
                                  onPlaceChange({ ...eventPlaceEntity, eventPlace })
                                }
                                label={'work.attr.event.attr.place'}
                              />
                            </InputGroup>
                          )}
                        />
                      </Fieldset>
                      <LabelledInputText
                        className={'w-12'}
                        id={`events-${idx}-numbering`}
                        value={event.eventNumbering}
                        permissions={checkPermission('event_numbering')}
                        validation={props.validation?.root?.fields?.events?.children[idx].fields?.event_numbering}
                        onChange={(eventNumbering: string): void => onEventChange({ ...event, eventNumbering })}
                        label={'work.attr.event.attr.numbering'}
                      />
                      <div className={'w-full grid'}>
                        <div className={'col-6 p-fluid'}>
                          <LabelledCalendar
                            id={`events-${idx}-startDate`}
                            value={event.startDate}
                            permissions={checkPermission('start_date')}
                            validation={props.validation?.root?.fields?.events?.children[idx].fields?.start_date}
                            onChange={(startDate: Date): void => onEventChange({ ...event, startDate })}
                            label={'work.attr.event.attr.startDate'}
                          />
                        </div>
                        <div className={'col-6 p-fluid'}>
                          <LabelledCalendar
                            id={`events-${idx}-endDate`}
                            value={event.endDate}
                            permissions={checkPermission('end_date')}
                            validation={props.validation?.root?.fields?.events?.children[idx].fields?.end_date}
                            onChange={(endDate: Date): void => onEventChange({ ...event, endDate })}
                            label={'work.attr.event.attr.endDate'}
                          />
                        </div>
                      </div>
                      <JustifiedDeleteButton onClick={actions.delete} disabled={!checkPermission().canWrite} />
                    </div>
                  </Fieldset>
                )}
              />
            </Panel>
          </div>
        )}
      </PermissionGate>
    </Block>
  );
};
